"use client";

import { useState, useEffect, useRef } from "react";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import { Toast } from "@/shared/ui/components/welcome-toast";
import Button from "@/shared/ui/components/button";
import { type AppContext, type CheckoutSession, CheckoutStatus } from "@/types";
import * as timeLib from "@/lib/time";
import { useCheckoutSessionV3 } from "@/modules/checkout-session";
import { useCosmosClient } from "@/services/cosmos";
import IvrMobileEng from "@/assets/ivr-mobile-eng.webp";
import { logError, logInfo } from "@/services/logging/logger";
import { useScreenTracking } from "@/shared/hooks/use-screen-tracking";
import * as events from "@/events";

interface IVRScreenProps {
  onSessionFetched: (session: CheckoutSession) => void;
}

const POLL_INTERVAL = 2 * 1000; // 2 seconds
const INITIAL_DELAY = 10 * 1000; // Start polling after 10 seconds
const MAX_RETRIES = 100;

enum Stage {
  POLLING,
  TIMED_OUT,
  FAILED,
  SUBMITTING_RETRY,
}

export function IVRScreen({ onSessionFetched }: IVRScreenProps) {
  const { data: session } = useCheckoutSessionV3();
  const [showCallingToast, setShowCallingToast] = useState(false);
  const [stage, setStage] = useState(Stage.POLLING);
  const [imageLoaded, setImageLoaded] = useState(false);
  const getCheckoutSessionRef = useRef<(() => Promise<CheckoutSession>) | null>(
    null,
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const isPollingRef = useRef(false);
  const ivrTimeoutRef = useRef<Date | null>(null);
  const stageRef = useRef<Stage>(Stage.POLLING);
  const client = useCosmosClient();
  const ctxRef = useRef<AppContext | null>(null);
  const [screenCtx, setScreenCtx] = useState<AppContext | null>(null);
  const hasInitializedScreenCtxRef = useRef(false);

  useEffect(() => {
    if (!hasInitializedScreenCtxRef.current && ctxRef.current) {
      setScreenCtx(ctxRef.current);
      hasInitializedScreenCtxRef.current = true;
    }
  });

  // Track screen view and exit for drop-off analysis
  useScreenTracking({
    screenName: "ivr_verification",
    viewedEvent: events.IVR_SCREEN_VIEWED,
    exitedEvent: events.IVR_SCREEN_EXITED,
    ctx: screenCtx,
  });

  const pollCheckoutSession = async () => {
    if (!getCheckoutSessionRef.current || stageRef.current !== Stage.POLLING) {
      return;
    }

    try {
      const session = await getCheckoutSessionRef.current();

      if (ivrTimeoutRef.current && timeLib.timePassed(ivrTimeoutRef.current)) {
        logInfo("ivr-screen", "IVR timed out", {
          sessionId: session?.id,
        });

        stageRef.current = Stage.TIMED_OUT;
        setStage(Stage.TIMED_OUT);
        stopPolling(); // Stop the interval when timed out

        ivrTimeoutRef.current = null;

        return;
      }

      if (ivrTimeoutRef.current == null && session.ivrTimeout > 0) {
        ivrTimeoutRef.current = timeLib.addSeconds(
          new Date(),
          session.ivrTimeout,
        );
      }

      // Reset retry count on successful fetch
      retryCountRef.current = 0;

      // Keep stage as POLLING (we know it's POLLING because we passed the guard at the top)
      // No need to reset since we already confirmed stageRef.current === Stage.POLLING
      stageRef.current = Stage.POLLING;
      setStage(Stage.POLLING);

      onSessionFetched(session);

      // Stop polling if status has changed
      if (session.status !== CheckoutStatus.AWAIT_IVR_CONFIRMATION) {
        stopPolling();
        return;
      }

      // Continue polling - interval will handle the next call
    } catch (err) {
      logError(
        "ivr-screen",
        "IVR polling error",
        err instanceof Error ? err : new Error(String(err)),
        { error: err, retryCount: retryCountRef.current },
      );

      retryCountRef.current += 1;

      if (retryCountRef.current >= MAX_RETRIES) {
        logError(
          "ivr-screen",
          `IVR polling failed after ${MAX_RETRIES} retries`,
          undefined,
          { retryCount: retryCountRef.current },
        );
        stopPolling();
        stageRef.current = Stage.FAILED;
        setStage(Stage.FAILED);
      }
      // If retries remain, the interval will retry on the next cycle
    }
  };

  const startPolling = () => {
    // Don't start if already polling or no session function available
    if (isPollingRef.current || !getCheckoutSessionRef.current) {
      return;
    }

    // Don't start if status is not awaiting IVR confirmation
    if (session?.status !== CheckoutStatus.AWAIT_IVR_CONFIRMATION) {
      return;
    }

    isPollingRef.current = true;
    retryCountRef.current = 0;

    // Initial delay before starting polling
    timeoutRef.current = setTimeout(() => {
      // First poll immediately
      pollCheckoutSession();

      // Then set up interval for subsequent polls
      intervalRef.current = setInterval(() => {
        pollCheckoutSession();
      }, POLL_INTERVAL);
    }, INITIAL_DELAY);
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isPollingRef.current = false;
  };

  const restartPolling = () => {
    stopPolling();
    retryCountRef.current = 0;
    stageRef.current = Stage.POLLING;
    setStage(Stage.POLLING);
    startPolling();
  };

  // Start polling when session status is AWAIT_IVR_CONFIRMATION
  // Also check periodically if getCheckoutSession becomes available
  useEffect(() => {
    if (session?.status !== CheckoutStatus.AWAIT_IVR_CONFIRMATION) {
      stopPolling();
      return;
    }

    // Try to start polling immediately if getCheckoutSession is available
    if (getCheckoutSessionRef.current) {
      startPolling();
      return;
    }

    // If getCheckoutSession isn't available yet, check periodically
    // (it gets set in the AppWrapper render callback)
    // We already know status is AWAIT_IVR_CONFIRMATION from the useEffect condition
    const checkInterval = setInterval(() => {
      if (getCheckoutSessionRef.current && !isPollingRef.current) {
        startPolling();
        clearInterval(checkInterval);
      }
    }, 100);

    return () => {
      clearInterval(checkInterval);
      stopPolling();
    };
  }, [session?.status]);

  // Handle page visibility changes (e.g., when phone call puts app in background)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Page became visible again - restart polling if we're still waiting for IVR
        // Check current session status (not from closure) to avoid stale state
        // Don't restart if we're in a terminal state (TIMED_OUT or FAILED)
        if (
          session?.status === CheckoutStatus.AWAIT_IVR_CONFIRMATION &&
          !isPollingRef.current &&
          stageRef.current !== Stage.TIMED_OUT &&
          stageRef.current !== Stage.FAILED
        ) {
          logInfo("ivr-screen", "Page became visible, restarting IVR polling", {
            sessionId: session?.id,
          });
          restartPolling();
        }
      }
      // When page goes to background, polling will continue but may be throttled
      // This is handled by the browser, but we ensure it resumes when visible
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // Note: We intentionally include session?.status in dependencies to ensure
    // we're checking the latest status, not a stale closure value
  }, [session?.status]);

  useEffect(() => {
    const showToastTimeoutId = setTimeout(() => {
      // TODO: we don't have yet the user's phone number to show on this toast. Fix later.
      // setShowCallingToast(true);
    }, 0);

    return () => {
      clearTimeout(showToastTimeoutId);
    };
  }, []);

  // Preload the image to prevent flash of missing content
  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(true); // Show content even if image fails
    img.src = IvrMobileEng;
  }, []);

  const failed = stage === Stage.FAILED;
  const timedOut = stage === Stage.TIMED_OUT;
  const submittingRetry = stage === Stage.SUBMITTING_RETRY;

  return (
    <AppWrapper
      visible={true}
      showLanguageSwitcher={true}
      showCartButton={false}
      headerBottomSpacing="none"
      stickyFooter={({ t }) =>
        failed || timedOut || submittingRetry ? (
          <Button
            size="large"
            loading={submittingRetry}
            onClick={async () => {
              if (failed) {
                restartPolling();

                return;
              }

              if (timedOut) {
                setStage(Stage.SUBMITTING_RETRY);

                try {
                  onSessionFetched(await client.retryIvr(session?.id ?? ""));

                  restartPolling();
                } catch (err) {
                  logError(
                    "ivr-screen",
                    "Error retrying IVR",
                    err instanceof Error ? err : new Error(String(err)),
                    { error: err, sessionId: session?.id },
                  );
                  setStage(Stage.FAILED);
                }
              }
            }}
          >
            {t("error.retry")}
          </Button>
        ) : (
          <Button loading size="large">
            {t("ivr.please_wait")}
          </Button>
        )
      }
    >
      {(ctx) => {
        const { t, getCheckoutSession } = ctx;
        // Store the getCheckoutSession function in the ref
        getCheckoutSessionRef.current = getCheckoutSession;
        ctxRef.current = ctx;

        return (
          <>
            <Toast
              show={showCallingToast}
              onComplete={() => setShowCallingToast(false)}
              type="calling"
              title={t("ivr.calling")}
              subtitle="050 XXX XXXX"
              duration={3000}
              position="top"
            />

            {/* Main Content */}
            <div>
              <div className="relative w-full">
                {!imageLoaded && (
                  <div
                    className="w-full h-auto bg-gray-100 animate-pulse"
                    style={{ aspectRatio: "16/9" }}
                  />
                )}
                <img
                  src={IvrMobileEng}
                  alt="IVR Mobile Interface"
                  className={`w-full h-auto transition-opacity duration-300 ${
                    imageLoaded ? "opacity-100" : "opacity-0 absolute inset-0"
                  }`}
                  loading="eager"
                  fetchPriority="high"
                  onLoad={() => setImageLoaded(true)}
                />
              </div>
              <div className="flex py-10 px-5 flex-col justify-center items-center gap-4 self-stretch bg-white">
                <h1 className="text-2xl font-bold text-gray-900 text-center">
                  {t("ivr.heading")}
                </h1>

                <p className="text-gray-700 text-lg text-center">
                  {t("ivr.press")}
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-[#9600F1] text-white rounded-full text-sm font-semibold mx-2">
                    1
                  </span>{" "}
                  {t("ivr.to_confirm")}
                </p>
              </div>

              {/* CTA moved to sticky footer via AppWrapper */}
            </div>
          </>
        );
      }}
    </AppWrapper>
  );
}
